'use client';

import React, { useState, useEffect } from 'react';
import StickyHeaderNav from '@/components/common/StickyHeaderNav';
import SectionAnchorNav from '@/components/common/SectionAnchorNav';
import HeroSection from './HeroSection';
import ProblemCalculator from './ProblemCalculator';
import SolutionShowcase from './SolutionShowcase';
import ComparisonTable from './ComparisonTable';
import VoiceAIDemo from './VoiceAIDemo';
import SuccessStories from './SuccessStories';
import ImplementationProcess from './ImplementationProcess';
import LeadCaptureForm from './LeadCaptureForm';
import FinalCTASection from './FinalCTASection';
import Footer from './Footer';

const LandingPageInteractive = () => {
  const [currentLanguage, setCurrentLanguage] = useState<'en' | 'es'>('en');
  const [currentSection, setCurrentSection] = useState('hero');
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
    
    // Load language from localStorage on mount
    const savedLanguage = localStorage.getItem('cognicore-language') as 'en' | 'es';
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  const handleLanguageChange = (language: 'en' | 'es') => {
    setCurrentLanguage(language);
    if (isHydrated) {
      localStorage.setItem('cognicore-language', language);
    }
  };

  const handleSectionChange = (sectionId: string) => {
    setCurrentSection(sectionId);
  };

  const handlePrimaryCTA = () => {
    // Scroll to lead capture form
    const element = document.getElementById('get-started');
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const handleSecondaryCTA = () => {
    // Scroll to features/comparison section instead of demo
    const element = document.getElementById('comparison');
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/landing-page/components/LandingPageInteractive.tsx:75:6" data-component-path="src/app/landing-page/components/LandingPageInteractive.tsx" data-component-line="75" data-component-end-line="81" data-component-file="LandingPageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-white%22%7D" className="min-h-screen bg-white">
        {/* Loading skeleton */}
        <div data-component-id="src/app/landing-page/components/LandingPageInteractive.tsx:77:8" data-component-path="src/app/landing-page/components/LandingPageInteractive.tsx" data-component-line="77" data-component-end-line="80" data-component-file="LandingPageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%22%7D" className="animate-pulse">
          <div data-component-id="src/app/landing-page/components/LandingPageInteractive.tsx:78:10" data-component-path="src/app/landing-page/components/LandingPageInteractive.tsx" data-component-line="78" data-component-end-line="78" data-component-file="LandingPageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-20%20bg-gray-200%22%7D" className="h-20 bg-gray-200"></div>
          <div data-component-id="src/app/landing-page/components/LandingPageInteractive.tsx:79:10" data-component-path="src/app/landing-page/components/LandingPageInteractive.tsx" data-component-line="79" data-component-end-line="79" data-component-file="LandingPageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-screen%20bg-gray-100%22%7D" className="h-screen bg-gray-100"></div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/landing-page/components/LandingPageInteractive.tsx:86:4" data-component-path="src/app/landing-page/components/LandingPageInteractive.tsx" data-component-line="86" data-component-end-line="136" data-component-file="LandingPageInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-white%22%7D" className="min-h-screen bg-white">
      {/* Sticky Header Navigation */}
      <StickyHeaderNav data-component-id="src/app/landing-page/components/LandingPageInteractive.tsx:88:6" data-component-path="src/app/landing-page/components/LandingPageInteractive.tsx" data-component-line="88" data-component-end-line="91" data-component-file="LandingPageInteractive.tsx" data-component-name="StickyHeaderNav" data-component-content="%7B%22elementName%22%3A%22StickyHeaderNav%22%7D"
        currentLanguage={currentLanguage}
        onLanguageChange={handleLanguageChange}
      />

      {/* Section Anchor Navigation */}
      <SectionAnchorNav data-component-id="src/app/landing-page/components/LandingPageInteractive.tsx:94:6" data-component-path="src/app/landing-page/components/LandingPageInteractive.tsx" data-component-line="94" data-component-end-line="97" data-component-file="LandingPageInteractive.tsx" data-component-name="SectionAnchorNav" data-component-content="%7B%22elementName%22%3A%22SectionAnchorNav%22%7D"
        currentLanguage={currentLanguage}
        onSectionChange={handleSectionChange}
      />

      {/* Hero Section */}
      <HeroSection data-component-id="src/app/landing-page/components/LandingPageInteractive.tsx:100:6" data-component-path="src/app/landing-page/components/LandingPageInteractive.tsx" data-component-line="100" data-component-end-line="105" data-component-file="LandingPageInteractive.tsx" data-component-name="HeroSection" data-component-content="%7B%22elementName%22%3A%22HeroSection%22%7D"
        currentLanguage={currentLanguage}
        onLanguageChange={handleLanguageChange}
        onPrimaryCTA={handlePrimaryCTA}
        onSecondaryCTA={handleSecondaryCTA}
      />

      {/* Problem Calculator */}
      <ProblemCalculator data-component-id="src/app/landing-page/components/LandingPageInteractive.tsx:108:6" data-component-path="src/app/landing-page/components/LandingPageInteractive.tsx" data-component-line="108" data-component-end-line="108" data-component-file="LandingPageInteractive.tsx" data-component-name="ProblemCalculator" data-component-content="%7B%22elementName%22%3A%22ProblemCalculator%22%7D" currentLanguage={currentLanguage} />

      {/* Solution Showcase */}
      <SolutionShowcase data-component-id="src/app/landing-page/components/LandingPageInteractive.tsx:111:6" data-component-path="src/app/landing-page/components/LandingPageInteractive.tsx" data-component-line="111" data-component-end-line="111" data-component-file="LandingPageInteractive.tsx" data-component-name="SolutionShowcase" data-component-content="%7B%22elementName%22%3A%22SolutionShowcase%22%7D" currentLanguage={currentLanguage} />

      {/* Comparison Table */}
      <ComparisonTable data-component-id="src/app/landing-page/components/LandingPageInteractive.tsx:114:6" data-component-path="src/app/landing-page/components/LandingPageInteractive.tsx" data-component-line="114" data-component-end-line="114" data-component-file="LandingPageInteractive.tsx" data-component-name="ComparisonTable" data-component-content="%7B%22elementName%22%3A%22ComparisonTable%22%7D" currentLanguage={currentLanguage} />

      {/* Voice AI Demo */}
      <VoiceAIDemo data-component-id="src/app/landing-page/components/LandingPageInteractive.tsx:117:6" data-component-path="src/app/landing-page/components/LandingPageInteractive.tsx" data-component-line="117" data-component-end-line="117" data-component-file="LandingPageInteractive.tsx" data-component-name="VoiceAIDemo" data-component-content="%7B%22elementName%22%3A%22VoiceAIDemo%22%7D" currentLanguage={currentLanguage} />

      {/* Success Stories */}
      <SuccessStories data-component-id="src/app/landing-page/components/LandingPageInteractive.tsx:120:6" data-component-path="src/app/landing-page/components/LandingPageInteractive.tsx" data-component-line="120" data-component-end-line="120" data-component-file="LandingPageInteractive.tsx" data-component-name="SuccessStories" data-component-content="%7B%22elementName%22%3A%22SuccessStories%22%7D" currentLanguage={currentLanguage} />

      {/* Implementation Process */}
      <ImplementationProcess data-component-id="src/app/landing-page/components/LandingPageInteractive.tsx:123:6" data-component-path="src/app/landing-page/components/LandingPageInteractive.tsx" data-component-line="123" data-component-end-line="123" data-component-file="LandingPageInteractive.tsx" data-component-name="ImplementationProcess" data-component-content="%7B%22elementName%22%3A%22ImplementationProcess%22%7D" currentLanguage={currentLanguage} />

      {/* Final CTA Section */}
      <FinalCTASection data-component-id="src/app/landing-page/components/LandingPageInteractive.tsx:126:6" data-component-path="src/app/landing-page/components/LandingPageInteractive.tsx" data-component-line="126" data-component-end-line="126" data-component-file="LandingPageInteractive.tsx" data-component-name="FinalCTASection" data-component-content="%7B%22elementName%22%3A%22FinalCTASection%22%7D" currentLanguage={currentLanguage} />

      {/* Lead Capture Form — single instance; duplicate copies used to share the
          same id="get-started", so any scroll-to-form button would always land
          on whichever copy came first in the DOM, regardless of which one the
          visitor actually meant to reach. */}
      <LeadCaptureForm data-component-id="src/app/landing-page/components/LandingPageInteractive.tsx:132:6" data-component-path="src/app/landing-page/components/LandingPageInteractive.tsx" data-component-line="132" data-component-end-line="132" data-component-file="LandingPageInteractive.tsx" data-component-name="LeadCaptureForm" data-component-content="%7B%22elementName%22%3A%22LeadCaptureForm%22%7D" currentLanguage={currentLanguage} />

      {/* Footer */}
      <Footer data-component-id="src/app/landing-page/components/LandingPageInteractive.tsx:135:6" data-component-path="src/app/landing-page/components/LandingPageInteractive.tsx" data-component-line="135" data-component-end-line="135" data-component-file="LandingPageInteractive.tsx" data-component-name="Footer" data-component-content="%7B%22elementName%22%3A%22Footer%22%7D" currentLanguage={currentLanguage} />
    </div>
  );
};

export default LandingPageInteractive;